import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, OnDestroy, Renderer2 } from '@angular/core';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

export interface AdminUser {
    id: string;
    name: string;
    email: string;
    initials: string;
    picture?: string;
}

@Component({
    selector: 'app-subtask-assignee-menu',
    templateUrl: './subtask-assignee-menu.component.html',
    styleUrls: ['./subtask-assignee-menu.component.scss'],
})
export class SubtaskAssigneeMenuComponent implements OnInit, OnDestroy {
    @Input() isVisible = false;
    @Input() position: { top: number; left: number } = { top: 0, left: 0 };
    @Input() selectedAssigneeId: string;
    @Input() preloadedAdminData: AdminUser[] = [];

    @Output() assigneeSelect = new EventEmitter<AdminUser | null>();
    @Output() close = new EventEmitter<void>();

    admins: AdminUser[] = [];
    filteredAdmins: AdminUser[] = [];
    cachedAdmins: { [query: string]: AdminUser[] } = {};
    lastSearchQuery = '';
    searchQuery = '';
    searchSubject = new Subject<string>();
    subscription: Subscription;
    loading = false;
    hasInitialData = false;

    constructor(
        private elementRef: ElementRef,
        private userService: UserService,
        private renderer: Renderer2,
    ) {}

    ngOnInit(): void {
        this.subscription = this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                filter((query) => (query !== this.lastSearchQuery && query.trim() !== '') || (query.trim() === '' && !this.hasInitialData)),
            )
            .subscribe((query) => {
                this.searchAdmins(query);
            });

        if (this.isVisible) {
            this.loadInitialData();
        }

        if (this.preloadedAdminData && this.preloadedAdminData.length > 0) {
            this.processPreloadedAdminData();
        }
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.isVisible && !this.elementRef.nativeElement.contains(event.target)) {
            this.closeMenu();
        }
    }

    open(position: { top: number; left: number }, selectedAssigneeId: string, preloadedAdminData: AdminUser[] = []): void {
        this.position = this.adjustMenuPosition(position);
        this.selectedAssigneeId = selectedAssigneeId;
        this.preloadedAdminData = preloadedAdminData;
        this.isVisible = true;

        if (preloadedAdminData && preloadedAdminData.length > 0) {
            this.processPreloadedAdminData();
        } else {
            this.loadInitialData();
        }
    }

    closeMenu(): void {
        this.isVisible = false;
        this.close.emit();
    }

    private adjustMenuPosition(position: { top: number; left: number }): { top: number; left: number } {
        const menu = this.elementRef.nativeElement;
        const menuWidth = 280;
        const menuHeight = 350;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        let { top, left } = position;

        if (left + menuWidth > windowWidth) {
            left = windowWidth - menuWidth - 10;
        }

        if (top + menuHeight > windowHeight) {
            top = top - menuHeight;
            if (top < 0) top = 10;
        }

        return { top, left };
    }

    private processPreloadedAdminData(): void {
        if (!this.preloadedAdminData || this.preloadedAdminData.length === 0) {
            return;
        }

        if (!this.cachedAdmins['']) {
            this.cachedAdmins[''] = [...this.preloadedAdminData];
        }

        this.admins = [...this.preloadedAdminData];
        this.hasInitialData = true;

        this.updateFilteredAdmins(this.admins);
    }

    onSearchChange(query: string): void {
        this.searchQuery = query;
        this.searchSubject.next(query);

        if (query.trim() === '' && this.cachedAdmins[''] && this.hasInitialData) {
            this.updateFilteredAdmins(this.cachedAdmins['']);
        }
    }

    loadInitialData(): void {
        if (this.cachedAdmins['']) {
            this.updateFilteredAdmins(this.cachedAdmins['']);
            return;
        }

        this.searchAdmins('');
    }

    searchAdmins(query: string): void {
        if (this.cachedAdmins[query]) {
            this.updateFilteredAdmins(this.cachedAdmins[query]);
            return;
        }

        this.loading = true;
        this.lastSearchQuery = query;

        this.userService.searchAdmins(query, 10).subscribe({
            next: (data) => {
                const searchResults = data.map((admin) => ({
                    id: admin.id || admin._id,
                    name: admin.name || `${admin.firstName} ${admin.lastName}`,
                    email: admin.email,
                    picture: admin.picture,
                    initials: this.getInitials(admin.name || `${admin.firstName} ${admin.lastName}`),
                }));

                this.cachedAdmins[query] = searchResults;

                if (query.trim() === '') {
                    this.hasInitialData = true;
                }

                this.updateFilteredAdmins(searchResults);
            },
            error: (error) => {
                this.admins = [];
                this.filteredAdmins = [];
                this.loading = false;
            },
        });
    }

    updateFilteredAdmins(searchResults: AdminUser[]): void {
        this.admins = searchResults;
        this.filteredAdmins = [...searchResults];
        this.loading = false;
    }

    onAssigneeSelect(admin: AdminUser): void {
        this.assigneeSelect.emit(admin);
        this.closeMenu();
    }

    onUnassignedSelect(): void {
        this.assigneeSelect.emit(null);
        this.closeMenu();
    }

    isAdminSelected(adminId: string): boolean {
        return this.selectedAssigneeId === adminId;
    }

    getInitials(name: string): string {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }
}
